import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../lib/AuthContext';
import { Toast } from '../components/InAppNotification';

export default function ProfilePage({ onViewChange }) {
    const { user } = useAuth();
    const [displayName, setDisplayName] = useState('');
    const [company, setCompany] = useState('');
    const [phone, setPhone] = useState('');
    const [jobTitle, setJobTitle] = useState('');
    const [website, setWebsite] = useState('');

    // Dashboard Defaults
    const [defaultViewMode, setDefaultViewMode] = useState('active');
    const [defaultScansPerPage, setDefaultScansPerPage] = useState(50);
    const [defaultDomainGrouping, setDefaultDomainGrouping] = useState(false);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);

    useEffect(() => {
        async function fetchProfile() {
            if (!user) return;
            try {
                const docSnap = await getDoc(doc(db, 'users', user.uid));
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setDisplayName(data.displayName || '');
                    setCompany(data.company || '');
                    setPhone(data.phone || '');
                    setJobTitle(data.jobTitle || '');
                    setWebsite(data.website || '');

                    const prefs = data.preferences || {};
                    setDefaultViewMode(prefs.defaultViewMode || 'active');
                    setDefaultScansPerPage(prefs.defaultScansPerPage || 50);
                    setDefaultDomainGrouping(prefs.defaultDomainGrouping || false);
                }
            } catch (err) {
                console.error("Error fetching profile:", err);
            } finally {
                setLoading(false);
            }
        }
        fetchProfile();
    }, [user]);

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await setDoc(doc(db, 'users', user.uid), {
                displayName: displayName.trim(),
                company: company.trim(),
                phone: phone.trim(),
                jobTitle: jobTitle.trim(),
                website: website.trim(),
                preferences: {
                    defaultViewMode,
                    defaultScansPerPage,
                    defaultDomainGrouping
                },
                updatedAt: serverTimestamp()
            }, { merge: true });
            setToast({ message: 'Profile updated successfully!', type: 'success' });
        } catch (err) {
            console.error("Error updating profile:", err);
            setToast({ message: 'Failed to update profile.', type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return (
        <div className="min-h-screen bg-(--bg) flex items-center justify-center">
            <div className="flex flex-col items-center gap-6">
                <div className="relative w-16 h-16">
                    <div className="absolute inset-0 border-4 border-(--accent)/20 rounded-full"></div>
                    <div className="absolute inset-0 border-4 border-(--accent) border-t-transparent rounded-full animate-spin"></div>
                </div>
                <p className="text-[0.625rem] text-(--accent) font-black uppercase tracking-[0.5em] animate-pulse">Loading Profile...</p>
            </div>
        </div>
    );

    return (
        <div className="flex-1 bg-(--bg) text-(--text) flex flex-col font-mono selection:bg-(--accent) selection:text-black">
            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

            <main className="max-w-6xl w-full mx-auto px-6 py-12 flex-1">
                <div className="mb-12">
                    <h1 className="text-3xl font-black text-(--text) uppercase tracking-[0.3em] ">User Profile Settings</h1>
                    <p className="text-[0.625rem] text-(--accent) font-black uppercase tracking-[0.2em] opacity-40 mt-2">User ID: {user?.uid.slice(0, 8)}</p>
                </div>

                <section className="bento-card bg-white p-10 relative">
                    <div className="absolute top-0 right-20 w-px h-10 bg-gradient-to-b from-(--accent) to-transparent opacity-30"></div>
                    <form onSubmit={handleSave} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 w-full">
                            <div className="space-y-8">
                                <h2 className="text-xs font-black text-(--accent) uppercase tracking-[0.4em] border-b pb-4 border-(--border)">Personal Information</h2>
                                <div className="space-y-3">
                                    <label className="text-[0.5625rem] font-black text-(--text-muted) uppercase tracking-[0.2em] ml-2">Full Name</label>
                                    <input
                                        type="text"
                                        value={displayName}
                                        onChange={(e) => setDisplayName(e.target.value)}
                                        className="w-full px-5 py-4 bg-(--surface-stone) border border-(--border) text-(--text) text-sm rounded-2xl focus:outline-none focus:border-(--accent) transition-all placeholder:opacity-50"
                                        placeholder="Enter your name"
                                    />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[0.5625rem] font-black text-(--text-muted) uppercase tracking-[0.2em] ml-2">Job Title</label>
                                    <input
                                        type="text"
                                        value={jobTitle}
                                        onChange={(e) => setJobTitle(e.target.value)}
                                        className="w-full px-5 py-4 bg-(--surface-stone) border border-(--border) text-(--text) text-sm rounded-2xl focus:outline-none focus:border-(--accent) transition-all placeholder:opacity-50"
                                        placeholder="e.g. Accessibility Specialist"
                                    />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[0.5625rem] font-black text-(--text-muted) uppercase tracking-[0.2em] ml-2">Phone Number</label>
                                    <input
                                        type="tel"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        className="w-full px-5 py-4 bg-(--surface-stone) border border-(--border) text-(--text) text-sm rounded-2xl focus:outline-none focus:border-(--accent) transition-all placeholder:opacity-50"
                                        placeholder="+1 000 000 0000"
                                    />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[0.5625rem] font-black text-(--text-muted) uppercase tracking-[0.2em] ml-2">Company / Organization</label>
                                    <input
                                        type="text"
                                        value={company}
                                        onChange={(e) => setCompany(e.target.value)}
                                        className="w-full px-5 py-4 bg-(--surface-stone) border border-(--border) text-(--text) text-sm rounded-2xl focus:outline-none focus:border-(--accent) transition-all placeholder:opacity-50"
                                        placeholder="Enter company name"
                                    />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[0.5625rem] font-black text-(--text-muted) uppercase tracking-[0.2em] ml-2">Primary Website</label>
                                    <input
                                        type="url"
                                        value={website}
                                        onChange={(e) => setWebsite(e.target.value)}
                                        className="w-full px-5 py-4 bg-(--surface-stone) border border-(--border) text-(--text) text-sm rounded-2xl focus:outline-none focus:border-(--accent) transition-all placeholder:opacity-50"
                                        placeholder="https://example.com"
                                    />
                                </div>
                            </div>

                            <div className="space-y-8">
                                <h2 className="text-xs font-black text-(--text) uppercase tracking-[0.4em] border-b pb-4 border-(--border)">Dashboard Preferences</h2>

                                <div className="space-y-4">
                                    <label className="text-[0.5625rem] font-black text-(--text-muted) uppercase tracking-[0.2em] ml-2">Default Dashboard Tab</label>
                                    <select
                                        value={defaultViewMode}
                                        onChange={(e) => setDefaultViewMode(e.target.value)}
                                        className="w-full px-5 py-4 bg-(--surface-stone) border border-(--border) text-(--text) text-sm rounded-2xl focus:outline-none focus:border-(--accent) transition-all cursor-pointer"
                                    >
                                        <option value="analytics" className="bg-(--bg)">Analytics</option>
                                        <option value="new-scan" className="bg-(--bg)">New Scan</option>
                                        <option value="scans" className="bg-(--bg)">Scans (History)</option>
                                        <option value="pages" className="bg-(--bg)">Pages</option>
                                        <option value="issues" className="bg-(--bg)">Issues</option>
                                        <option value="journeys" className="bg-(--bg)">User Journeys</option>
                                        <option value="reports" className="bg-(--bg)">Reports</option>
                                    </select>
                                    <p className="text-[0.5625rem] text-(--text-muted) mt-2 font-black uppercase tracking-widest opacity-40">Loads this tab on login.</p>
                                </div>

                                <div className="space-y-4">
                                    <label className="text-[0.5625rem] font-black text-(--text-muted) uppercase tracking-[0.2em] ml-2">Scans Per Page</label>
                                    <select
                                        value={defaultScansPerPage}
                                        onChange={(e) => setDefaultScansPerPage(e.target.value === 'All' ? 'All' : Number(e.target.value))}
                                        className="w-full px-5 py-4 bg-(--surface-stone) border border-(--border) text-(--text) text-sm rounded-2xl focus:outline-none focus:border-(--accent) transition-all cursor-pointer"
                                    >
                                        <option value={5} className="bg-(--bg)">5 Scans</option>
                                        <option value={10} className="bg-(--bg)">10 Scans</option>
                                        <option value={50} className="bg-(--bg)">50 Scans</option>
                                        <option value={100} className="bg-(--bg)">100 Scans</option>
                                        <option value="All" className="bg-(--bg)">Show All</option>
                                    </select>
                                </div>

                                <div className="space-y-4 pt-4">
                                    <label className="flex items-center gap-4 cursor-pointer group">
                                        <div className="relative h-6 w-6 border border-(--border) bg-(--bg) rounded flex items-center justify-center group-hover:border-(--accent)/50 transition-all">
                                            <input
                                                type="checkbox"
                                                checked={defaultDomainGrouping}
                                                onChange={(e) => setDefaultDomainGrouping(e.target.checked)}
                                                className="h-3 w-3 appearance-none checked:bg-(--accent) transition-all cursor-pointer rounded-sm"
                                            />
                                        </div>
                                        <span className="text-[0.625rem] font-black text-(--text-muted) uppercase tracking-[0.2em] group-hover:text-(--text) transition-colors">Group by Domain</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="pt-10 flex items-center gap-6 border-t border-(--border)">
                            <button
                                type="submit"
                                disabled={saving}
                                className="pill-button px-10 py-5 text-[0.75rem]"
                            >
                                {saving ? (
                                    <>
                                        <div className="animate-spin w-4 h-4 border-2 border-white opacity-40 border-t-white rounded-full" />
                                        <span>Saving Variables...</span>
                                    </>
                                ) : 'Save Profile'}
                            </button>
                            <div className="text-[0.625rem] text-(--text-muted) font-black uppercase tracking-[0.3em] opacity-40">
                                Ready
                            </div>
                        </div>
                    </form>
                </section>
            </main>
        </div>
    );
}
